import { withBackend } from '../utils/api';

// ── OAuth Connection ──────────────────────────────────────────────────────────

export async function checkConnection(userId, provider) {
  const resp = await fetch(withBackend(`/api/oauth/${provider}/status?user_id=${userId}`));
  return resp.json();
}

export async function connectDrive(userId, provider) {
  // Get the authorization URL from the backend
  const resp = await fetch(withBackend(`/api/oauth/${provider}/authorize?user_id=${userId}`));
  const { authorize_url, error } = await resp.json();
  if (error) throw new Error(error);

  // Open popup
  return new Promise((resolve, reject) => {
    const popup = window.open(authorize_url, `${provider}_oauth`, 'width=500,height=650,scrollbars=yes');
    if (!popup) {
      reject(new Error('Popup blocked — please allow popups for this site'));
      return;
    }

    let settled = false;
    const cleanup = () => {
      window.removeEventListener('message', handler);
      clearTimeout(timeoutTimer);
    };

    const handler = (event) => {
      if (event.data?.type !== 'OAUTH_CALLBACK') return;
      if (settled) return;
      settled = true;
      cleanup();
      if (event.data.status === 'success') {
        resolve({ success: true, provider });
      } else {
        reject(new Error(event.data.message || 'Authorization failed'));
      }
    };
    window.addEventListener('message', handler);

    // Fallback timeout: if no postMessage within 10 minutes, re-check status
    const timeoutTimer = setTimeout(async () => {
      if (settled) return;
      settled = true;
      cleanup();
      try {
        const status = await checkConnection(userId, provider);
        if (status.connected) resolve({ success: true, provider });
        else reject(new Error('Authorization timed out or was cancelled'));
      } catch {
        reject(new Error('Authorization timed out'));
      }
    }, 10 * 60 * 1000);
  });
}

export async function disconnectDrive(userId, provider) {
  await fetch(withBackend(`/api/oauth/${provider}/disconnect`), {
    method: 'DELETE',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ user_id: userId }),
  });
}

export async function getGoogleAccessToken(userId) {
  const resp = await fetch(withBackend('/api/oauth/google/refresh'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ user_id: userId }),
  });
  const data = await resp.json();
  if (data.error) throw new Error(data.error);
  return data.access_token;
}

// ── File Listing & Search ─────────────────────────────────────────────────────

export async function listFiles(userId, provider, { folderId = 'root', itemId = 'root', pageToken, nextLink, mimeFilter } = {}) {
  if (provider === 'google') {
    const params = new URLSearchParams({ user_id: userId, folder_id: folderId });
    if (pageToken) params.set('page_token', pageToken);
    if (mimeFilter) params.set('mime_filter', mimeFilter);
    const resp = await fetch(withBackend(`/api/drive/google/list?${params}`));
    return resp.json();
  } else {
    const params = new URLSearchParams({ user_id: userId, item_id: itemId });
    if (nextLink) params.set('next_link', nextLink);
    const resp = await fetch(withBackend(`/api/drive/microsoft/list?${params}`));
    return resp.json();
  }
}

export async function searchFiles(userId, provider, query) {
  const params = new URLSearchParams({ user_id: userId, query });
  const resp = await fetch(withBackend(`/api/drive/${provider}/search?${params}`));
  return resp.json();
}

// ── File Content Extraction ───────────────────────────────────────────────────

export async function getFileContent(userId, provider, file) {
  if (provider === 'google') {
    const resp = await fetch(withBackend('/api/drive/google/content'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ user_id: userId, file_id: file.id, mime_type: file.mimeType, file_name: file.name }),
    });
    return resp.json();
  } else {
    const resp = await fetch(withBackend('/api/drive/microsoft/content'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ user_id: userId, item_id: file.id, file_name: file.name, download_url: file.downloadUrl }),
    });
    return resp.json();
  }
}
